import AddCategoryModal from "@/src/components/action-modals/add-category-modal";
import { CategoryTag } from "@/src/models/category.model";
import {
  useGetCategoriesQuery,
  useInsertCategoryMutation,
} from "@/src/services/api/category/category.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { Add, CloseCircle } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, TouchableOpacity, View } from "react-native";
import CategoryChipSkeleton from "./category-skeleton";

const CategorySelectorSkeleton = () => (
  <View className="flex-row flex-wrap gap-2">
    {[80, 100, 70, 90, 60].map((_, index) => (
      <CategoryChipSkeleton key={index} />
    ))}
  </View>
);

type CategorySelectorProps = Readonly<{
  label?: string;
  entityType: CategoryTag;
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  maxCategories?: number;
}>;

export default function CategorySelector({
  label = "Category",
  entityType,
  selectedIds,
  onSelectedIdsChange,
  maxCategories = 4,
}: CategorySelectorProps) {
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  const { data: fetchedCategories, isLoading } = useGetCategoriesQuery({
    tag: entityType,
    pageSize: 1000,
  });
  const { mutate: insertCategory, isPending: isInserting } =
    useInsertCategoryMutation();

  const [modalVisible, setModalVisible] = useState(false);

  const canAddMore = selectedIds.length < maxCategories;

  const handleToggle = (itemId: string) => {
    const isSelected = selectedIds.includes(itemId);
    const updated = isSelected
      ? selectedIds.filter((id) => id !== itemId)
      : [...selectedIds, itemId];
    onSelectedIdsChange(updated);
  };

  const handleAdd = (categoryName: string) => {
    insertCategory(
      { categoryName, tags: [entityType] },
      {
        onSuccess: (result) => {
          onSelectedIdsChange([...selectedIds, result.itemId]);
          setModalVisible(false);
        },
      },
    );
  };

  return (
    <View className="mb-4">
      <Text className="pb-4 font-medium text-onyx">{label}</Text>

      {isLoading ? (
        <CategorySelectorSkeleton />
      ) : (
        <View className="flex-row flex-wrap gap-2 mb-3">
          {fetchedCategories?.items.map((category) => {
            const isSelected = selectedIds.includes(category.ItemId);
            return isSelected ? (
              <View
                key={category.ItemId}
                className="flex-row items-center bg-primary-2 rounded-full p-2 gap-2"
              >
                <Text className="text-primary font-medium">
                  {category.categoryName}
                </Text>
                <TouchableOpacity
                  onPress={() => handleToggle(category.ItemId)}
                  className="w-6 h-6 rounded-full items-center justify-center hover:bg-primary-2"
                  activeOpacity={0.7}
                >
                  <CloseCircle
                    size={24}
                    color={themeVars["--color-primary-default"] as string}
                  />
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                key={category.ItemId}
                onPress={() => canAddMore && handleToggle(category.ItemId)}
                activeOpacity={0.7}
                className={`flex-row items-center rounded-full px-3 py-2 gap-1 border border-ash-3 bg-chalk hover:bg-ash-3 ${
                  !canAddMore ? "opacity-40" : ""
                }`}
              >
                <Text className="text-sm font-medium text-slate">
                  {category.categoryName}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {canAddMore && (
        <TouchableOpacity
          onPress={() => setModalVisible(true)}
          className="flex-row items-center border-2 border-dashed border-ash-3 rounded-lg p-2 gap-2 self-start mt-1 hover:bg-ash-3"
          activeOpacity={0.7}
        >
          <Add size={24} color={themeVars["--color-slate-default"] as string} />
          <Text className="text-slate font-medium text-base">
            {t("LABEL_ADD_CATEGORY")}
          </Text>
        </TouchableOpacity>
      )}

      <AddCategoryModal
        visible={modalVisible}
        onAdd={handleAdd}
        onCancel={() => setModalVisible(false)}
        loading={isInserting}
        fixedTag={entityType}
      />
    </View>
  );
}
